/**
 * Règles géographiques : coordonnées, distance, affichage.
 *
 * Le calcul est fait ici et non en base : Aurora DSQL n'a pas PostGIS, et le
 * filtrage côté serveur se fera par encadrement latitude / longitude (lot 2).
 * Un encadrement donne un carré, pas un cercle ; c'est cette fonction qui
 * départage les candidats et les classe.
 */

export interface Coordinates {
  readonly latitude: number;
  readonly longitude: number;
}

const EARTH_RADIUS_KM = 6371;

function toRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

/**
 * Distance orthodromique entre deux points, en kilomètres.
 *
 * Formule de haversine : à l'échelle d'un secteur de vente, l'écart avec un
 * calcul ellipsoïdal se compte en mètres, largement sous la précision d'une
 * distance annoncée au dixième de kilomètre. Reste une distance à vol d'oiseau
 * et non routière — c'est pourquoi l'affichage la présente comme approchée.
 */
export function distanceBetween(from: Coordinates, to: Coordinates): number {
  const deltaLatitude = toRadians(to.latitude - from.latitude);
  const deltaLongitude = toRadians(to.longitude - from.longitude);

  const half =
    Math.sin(deltaLatitude / 2) ** 2 +
    Math.cos(toRadians(from.latitude)) *
      Math.cos(toRadians(to.latitude)) *
      Math.sin(deltaLongitude / 2) ** 2;

  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(half)));
}

const KM = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 });

/**
 * Distance à la française, en mètres sous le kilomètre.
 *
 * « 800 m » se lit mieux que « 0,8 km », et c'est la valeur que lit d'abord un
 * client qui cherche un producteur à côté de chez lui.
 */
export function formatDistance(km: number): string {
  if (km < 1) {
    return `${Math.round(km * 1000)} m`;
  }
  return `${KM.format(km)} km`;
}
